import { useState } from 'react';
import { debounce } from 'lodash/fp';
import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { FormSubmitHandler, reset } from 'redux-form';
import { TextKey } from '@mews-commander/enums';
import { CommanderThunkDispatch } from '@mews-commander/types';
import { LocalizedText, lt, showSuccessAlert } from '@mews/framework';
import { FieldsContainer, LocalizedSectionTitle, LocalizedTypography } from '@mews-commander/ui';
import { Stack } from '@optimus-web/core';
import { InputValue } from '@optimus-web/common';
import { UnicodeEntity } from '@mews/enums';
import {
    commanderForm,
    DropzoneField,
    InputField,
    maxInputTextLength,
    maxLongInputTextLength,
    presence,
    RadioGroupField,
    SelectField,
    TagInputField,
    TextareaField,
    validateSingle,
    validation,
} from '@mews-commander/legacy-form';
import { MINIMAL_SEARCH_TOKEN_LENGTH } from '../constants';
import { sanitazeSearchValue } from '../utils';
import { ExtendedTicketFormValues } from '../types';
import { submitExtendedSupportTicket } from '../actions';
import { ArticleSuggestion } from './ArticleSuggestion';
import {
    categoryProps,
    collaboratorsProps,
    descriptionProps,
    priorityProps,
    reasonProps,
    SalesforceKeys,
} from './fieldProps';
import { fileProps, titleProps } from './TicketForm';

interface DispatchProps {
    onSubmit: FormSubmitHandler<ExtendedTicketFormValues>;
}

const FORM_NAME = 'helpCenterExtendedTicketForm';

const constraints = {
    [SalesforceKeys.TITLE]: { presence, length: maxInputTextLength },
    [SalesforceKeys.REASON]: { presence },
    [SalesforceKeys.DESCRIPTION]: { presence, length: maxLongInputTextLength },
    [SalesforceKeys.CATEGORY]: { presence },
    [SalesforceKeys.PRIORITY]: { presence },
    [SalesforceKeys.COLLABORATORS]: {
        emails: true,
    },
};

const ExtendedTicketFormFields = () => {
    const [titleValue, setTitleValue] = useState('');
    const [invalidTags, setInvalidTags] = useState<string[]>([]);
    const [emailsValue, setEmailsValue] = useState<string[]>([]);

    const debounceInputValueChange = debounce(
        1000,
        (event: React.ChangeEvent<HTMLInputElement> | React.MouseEvent, value: InputValue) => {
            const sanitizedValue = sanitazeSearchValue(value.toString());
            setTitleValue(sanitizedValue);
        }
    );
    const handleEmailsChange = (_: React.SyntheticEvent | undefined, newValue: string[]) => {
        setEmailsValue(newValue);
        const validationResult = validateSingle(newValue, constraints[SalesforceKeys.COLLABORATORS]);
        if (validationResult) {
            const { invalidEmails } = validationResult;
            setInvalidTags(invalidEmails);
        }
    };

    return (
        <Stack spacing="100" vertical>
            <LocalizedSectionTitle textKey={TextKey.CanHelpTitle} />
            <LocalizedTypography textStyle="bodySmall" textKey={TextKey.CanHelpSubtitle} as="div" />
            <FieldsContainer>
                <InputField {...titleProps} onChange={debounceInputValueChange} />
                {titleValue.length >= MINIMAL_SEARCH_TOKEN_LENGTH && <ArticleSuggestion searchToken={titleValue} />}
                <RadioGroupField {...reasonProps} />
                <SelectField {...categoryProps} />
                <RadioGroupField {...priorityProps} />
                <TextareaField {...descriptionProps} />
                <DropzoneField {...fileProps} />
                <TagInputField
                    {...collaboratorsProps}
                    onChange={handleEmailsChange}
                    value={emailsValue}
                    invalidChips={invalidTags}
                    delimiters={[
                        UnicodeEntity.Space,
                        UnicodeEntity.Comma,
                        UnicodeEntity.Semicolon,
                        UnicodeEntity.NewLine,
                    ]}
                />
            </FieldsContainer>
        </Stack>
    );
};

export default pipe(
    ExtendedTicketFormFields,
    commanderForm({
        destroyOnUnmount: false,
        submitLabel: <LocalizedText textKey={TextKey.Send} />,
        validate: validation(constraints),
        submitButtonProps: { variant: 'primary' },
        form: FORM_NAME,
        initialValues: { collaborators: [] },
    }),
    connect<{}, DispatchProps>(undefined, (dispatch: CommanderThunkDispatch) => ({
        onSubmit: async (values) => {
            await dispatch(submitExtendedSupportTicket(values));
            dispatch(reset(FORM_NAME));
            showSuccessAlert({ description: lt('HelpCenterTicketFormSuccessMessage') });
        },
    }))
);
